import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Loader from '../components/Loader';
import styled from "styled-components";
import FilterBar from '../components/filterBarAlternate';
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate } from 'react-router-dom';

const Row = styled.div`
  display: flex;
  gap: 22px;
  margin-bottom: 0.3rem;
`;
const styles = {
  pageBody: {
    padding: 20,
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    maxWidth: 1400,
    margin: "auto",
    backgroundColor: "#fafafa",
    borderRadius: 12,
    boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
    color: "#222",
    minHeight: "80vh",
    marginTop:'50px'
  },
  tabBar: {
    display: "flex",
    marginBottom: 32,
    gap: 12,
    borderBottom: "3px solid #e0e0e0",
    userSelect: "none",
  },
  tabButton: (active) => ({
    padding: "14px 32px",
    cursor: "pointer",
    border: "none",
    backgroundColor: active ? "#0056b3" : "#e8e8e8",
    color: active ? "white" : "#444",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    fontWeight: "700",
    fontSize: 17,
    boxShadow: active ? "0 5px 15px rgba(0,86,179,0.4)" : "none",
    transition: "background-color 0.3s ease, box-shadow 0.3s ease",
    userSelect: "none",
  }),
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0 12px",
    fontSize: 15,
    color: "#333",
    tableLayout: "fixed",
    borderRadius: 10,
  },
  th: {
    padding: "16px 20px",
    textAlign: "left",
    fontWeight: "700",
    fontSize: 16,
    color: "#222",
    backgroundColor: "#f2f7fe",
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    userSelect: "none",
    wordWrap: "break-word",
  },
  td: {
    padding: "14px 20px",
    backgroundColor: "white",
    borderBottom: "12px solid transparent",
    color: "#444",
    fontWeight: "500",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  tr: {
    transition: "background-color 0.2s ease",
  },
  button: {
    padding: "10px 22px",
    marginRight: 10,
    cursor: "pointer",
    borderRadius: 8,
    border: "1.8px solid #0056b3",
    backgroundColor: "white",
    color: "#0056b3",
    fontWeight: "700",
    fontSize: 15,
    transition: "all 0.3s ease",
    userSelect: "none",
    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
    outline: "none",
  },
  pageButton: {
    padding: "8px 16px",
    cursor: "pointer",
    borderRadius: 6,
    border: "1px solid #0056b3",
    backgroundColor: "white",
    color: "#0056b3",
    fontWeight: "600",
    transition: "all 0.2s ease",
  },
  pageButtonDisabled: {
    padding: "8px 16px",
    cursor: "not-allowed",
    borderRadius: 6,
    border: "1px solid #ccc",
    backgroundColor: "#f5f5f5",
    color: "#aaa",
    fontWeight: "600",
  },
  modalOverlay: {
    position: "fixed",
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(0,0,0,0.55)",
    zIndex: 10000,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: 14,
    padding: 36,
    width:'100%',
    maxWidth: "900px",
    maxHeight: "85vh",
    overflowY: "auto",
    boxShadow: "0 10px 30px rgba(0,0,0,0.22)",
    fontSize: 16,
    color: "#111",
    display: "flex",
    flexDirection: "column",
    userSelect: "text",
  },
  label: {
    display: "block",
    fontWeight: "700",
    marginTop: 20,
    marginBottom: 8,
    color: "#333",
    fontSize: 16,
  },
  input: {
    width: "100%",
    padding: "14px 18px",
    borderRadius: 8,
    border: "1.8px solid #bfc6d4",
    backgroundColor: "#f7faff",
    fontSize: 16,
    color: "#374151",
    fontWeight: "600",
    boxSizing: "border-box",
  },
  textarea: {
    resize: "vertical",
    minHeight: 100,
    maxHeight: 160,
    padding: "14px 18px",
    fontSize: 16,
    color: "#374151",
    borderRadius: 8,
    border: "1.8px solid #bfc6d4",
    backgroundColor: "#f7faff",
    fontWeight: "600",
    boxSizing: "border-box",
  },
  denominationRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 14,
    padding: "8px 20px",
    backgroundColor: "#f9fbff",
    borderRadius: 10,
    fontWeight: "600",
  },
  denomAmount: { fontWeight: "800", color: "#004a99", minWidth: 70, fontSize: 16 },
  denomCount: { minWidth: 85, textAlign: "center", fontSize: 18, fontWeight: "700", color: "#0071e3" },
  denomTotal: { fontWeight: "800", color: "#1a1a1a", minWidth: 100, textAlign: "right", fontSize: 18 },
  sectionTitle: { fontWeight: "900", fontSize: "1.4rem", marginTop: 36, marginBottom: 16, borderBottom: "4px solid #004a99", paddingBottom: 6, color: "#003366" },
  summaryRow: { display: "flex", justifyContent: "space-between", marginTop: 16, fontWeight: "900", fontSize: 18, color: "#222" },
};

const numberDisplay = (val) => (val === null || val === undefined ? '0' : Number(val).toFixed(1));

function ReportsPage() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('access_token') || '';
  const headers = { Authorization: `Bearer ${token}` };

  const [activeTab, setActiveTab] = useState('opening');
  
  // Data States
  const [openingReports, setOpeningReports] = useState([]);
  const [closingReports, setClosingReports] = useState([]);
  const [combinedReports, setCombinedReports] = useState([]);
  const [stores, setStores] = useState(null);
  
  // UI States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  
  // Filter States
  const [filterText, setFilterText] = useState("");
  const [filterFromDate, setFilterFromDate] = useState(null);
  const [filterToDate, setFilterToDate] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [filterStoreId, setFilterStoreId] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit, setLimit] = useState(10); // Defaults to 10

  const userData = { user: JSON.parse(localStorage.getItem('user')), token: localStorage.getItem('access_token') };
  const isAdmin = userData.user.roles.role_name === "SUPER_ADMIN";
  const navigate = useNavigate();
  const hostname = window.location.hostname;
  const isInventoryDomain = hostname === 'inventory.fescafe.in' || hostname === 'localhost';
  const isRecordsDomain = hostname === 'records.fescafe.in' ;

  const fetchStores = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/store`, { headers });
      setStores(res.data.sort((a, b) => a.id - b.id));
    } catch (err) {
      console.log("Error fetching stores:", err.message);
    }
  };

  useEffect(() => {
    fetchStores();
    if(!isAdmin) navigate(isRecordsDomain ? '/cashflow' : '/inventory/entries');
  }, []);

  const handleStoreFilter = useCallback((id) => { setFilterStoreId(id || ""); setCurrentPage(1); }, []);
  const handleTextFilter = useCallback((text) => { setFilterText(text || ""); setCurrentPage(1); }, []);
  const handleDateFilter = useCallback((from, to) => { setFilterFromDate(from || null); setFilterToDate(to || null); setCurrentPage(1); }, []);
  const handleSortChange = useCallback((field, order) => { setSortField(field); setSortOrder(order); setCurrentPage(1); }, []);
  const handleLimitChange = useCallback((e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }, []);
  
  const handleResetFilters = useCallback(() => {
    setFilterStoreId(null);
    setFilterText("");
    setFilterFromDate(null);
    setFilterToDate(null);
    setSortField(null);
    setSortOrder(null);
    setCurrentPage(1);
  }, []);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setCurrentPage(1); 
  }, []);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    const params = new URLSearchParams({ page: currentPage, limit: limit });
    if (filterText) params.append('search', filterText);
    if (filterStoreId) params.append('storeId', filterStoreId);
    if (filterFromDate) params.append('fromDate', filterFromDate);
    if (filterToDate) params.append('toDate', filterToDate);
    if (sortField) params.append('sortField', sortField);
    if (sortOrder) params.append('sortOrder', sortOrder);

    try {
      if (activeTab === 'opening' || activeTab === 'banking') {
        const res = await axios.get(`${BACKEND_URL}/reports/opening/paginated?${params.toString()}`, { headers });
        setOpeningReports(res.data.data);
        setTotalRecords(res.data.count || 0);
      } else if (activeTab === 'closing') {
        const res = await axios.get(`${BACKEND_URL}/reports/closing/paginated?${params.toString()}`, { headers });
        setClosingReports(res.data.data);
        setTotalRecords(res.data.count || 0);
      } else {
        const res = await axios.get(`${BACKEND_URL}/reports/combined/paginated?${params.toString()}`, { headers });
        setCombinedReports(res.data.data);
        setTotalRecords(res.data.count || 0);
      }
    } catch (e) {
      setError('Failed to fetch reports, please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage, limit, filterText, filterStoreId, filterFromDate, filterToDate, sortField, sortOrder]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // NEW EXPORT HELPER: Fetch all records bypassing pagination constraints for exports
  const fetchAllFilteredData = async () => {
    let allData = [];
    let currentPageNum = 1;
    const fetchLimit = 1000; // Fetch in chunks of 1000
    let hasMore = true;

    while (hasMore) {
      const params = new URLSearchParams({ page: currentPageNum, limit: fetchLimit });
      if (filterText) params.append('search', filterText);
      if (filterStoreId) params.append('storeId', filterStoreId);
      if (filterFromDate) params.append('fromDate', filterFromDate);
      if (filterToDate) params.append('toDate', filterToDate);
      if (sortField) params.append('sortField', sortField);
      if (sortOrder) params.append('sortOrder', sortOrder);

      let endpoint = '';
      if (activeTab === 'opening' || activeTab === 'banking') endpoint = '/reports/opening/paginated';
      else if (activeTab === 'closing') endpoint = '/reports/closing/paginated';
      else endpoint = '/reports/combined/paginated';

      const res = await axios.get(`${BACKEND_URL}${endpoint}?${params.toString()}`, { headers });
      const batch = res.data.data || [];
      allData = [...allData, ...batch];

      if (batch.length < fetchLimit) {
        hasMore = false;
      } else {
        currentPageNum++;
      }
    }
    return allData;
  };

  const openViewModal = (report) => { setSelectedReport(report); setViewModalOpen(true); };
  const closeViewModal = () => { setSelectedReport(null); setViewModalOpen(false); };

  const DENOMS = [2000, 500, 200, 100, 50, 20, 10];
  
  const DenomRows = ({ jsonData }) => {
    if (!jsonData) return <div>No data</div>;
    return (
      <>
        {Object.keys(jsonData).map(Number).sort((a, b) => b - a).map((denom) => {
          const count = jsonData[denom] || 0;
          return (
            <div key={denom} style={styles.denominationRow}>
              <div style={styles.denomAmount}>₹{denom} x</div>
              <div style={styles.denomCount}>{numberDisplay(count)}</div>
              <div style={styles.denomTotal}>₹{numberDisplay(denom * count)}</div>
            </div>
          );
        })}
      </>
    );
  };

  const calculateTotal = (jsonData) => {
    if (!jsonData) return 0;
    return DENOMS.reduce((acc, denom) => acc + denom * (jsonData[denom] || 0), 0);
  };

  const calculateSalesTotal = (salesData) => {
    if (!salesData) return 0;
    return Object.values(salesData).reduce((acc, val) => acc + (Number(val) || 0), 0);
  };

  // Rendering row functions
  const renderOpeningRows = () =>
    openingReports.map((r) => (
      <tr key={r.id} style={styles.tr} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e8f0fe')} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}>
        <td style={styles.td}>{r.report_date}</td>
        <td style={styles.td}>{ `${r.store_data?.friendly_name || ''}, ${r.store_data?.city || ''}, ${r.store_data?.state || ''}`}</td>
        <td style={styles.td}>{`${r.user_data?.first_name || ''} ${r.user_data?.last_name || ''}`}</td>
        <td style={styles.td}>
          <button style={styles.button} onClick={() => openViewModal(r)}>View</button>
        </td>
      </tr>
    ));

  const renderClosingRows = () =>
    closingReports.map((r) => (
      <tr key={r.id} style={styles.tr} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e8f0fe')} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}>
        <td style={styles.td}>{r.report_date}</td>
        <td style={styles.td}>{ `${r.store_data?.friendly_name || ''}, ${r.store_data?.city || ''}, ${r.store_data?.state || ''}`}</td>
        <td style={styles.td}>{`${r.user_data?.first_name || ''} ${r.user_data?.last_name || ''}`}</td>
        <td style={styles.td}>
          <button style={styles.button} onClick={() => openViewModal(r)}>View</button>
        </td>
      </tr>
    ));
    
  const renderCombinedRows = () =>
    combinedReports.map((item, index) => {
      const { opening_report, closing_report } = item;
      const date = opening_report?.report_date || closing_report?.report_date || "";
      const storeName = opening_report?.store_data?.friendly_name || closing_report?.store_data?.friendly_name || "Unknown";
      const userNameOpening = opening_report?.user_data ? `${opening_report.user_data.first_name} ${opening_report.user_data.last_name}` : "";
      const userNameClosing = closing_report?.user_data ? `${closing_report.user_data.first_name} ${closing_report.user_data.last_name}` : "";

      return (
        <tr key={index} style={styles.tr} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e8f0fe')} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}>
          <td style={styles.td}>{date}</td>
          <td style={styles.td}>{storeName}</td>
          <td style={styles.td}>{userNameOpening}</td>
          <td style={styles.td}>{userNameClosing}</td>
          <td style={styles.td}>
            <button style={styles.button} onClick={() => openViewModal(item)}>View</button>
          </td>
        </tr>
      );
    });

  const ModalContent = () => {
    if (!selectedReport) return null;
    if (activeTab === 'banking') {
      const r = selectedReport;
      const totalOpeningCash = calculateTotal(r.opening_currency_count);
      const totalBankingCash = calculateTotal(r.banking_currency_count);

      return (
        <>
          <h3 style={styles.sectionTitle}>Opening Report Details</h3>
          <label style={styles.label}>Date</label>
          <input type="date" value={r.report_date} style={styles.input} disabled />
          <label style={styles.label}>Store</label>
          <input value={`${r.store_data?.friendly_name}, ${r.store_data?.city}, ${r.store_data?.state}`} style={styles.input} disabled />
          <label style={styles.label}>Created By</label>
          <input value={`${r.user_data?.first_name} ${r.user_data?.last_name}` || ''} style={styles.input} disabled />
           
          <div style={styles.sectionTitle}>Banking Currency Count</div>
          <DenomRows jsonData={r.banking_currency_count} />
          
          <div style={styles.summaryRow}>
            <div style={{fontSize : '25px'}}>Total Banking Cash</div>
            <div style={{fontSize : '25px'}}>₹{numberDisplay(totalBankingCash)}</div>
          </div>

          <label style={styles.label}>Status</label>
          <input value={r.status || ''} style={styles.input} disabled />
        </>
      );
    } 
    else if (activeTab === 'opening') {
      const r = selectedReport;
      const totalOpeningCash = calculateTotal(r.opening_currency_count);
      const totalBankingCash = calculateTotal(r.banking_currency_count);

      return (
        <>
          <h3 style={styles.sectionTitle}>Opening Report Details</h3>
          <label style={styles.label}>Date</label>
          <input type="date" value={r.report_date} style={styles.input} disabled />
          <label style={styles.label}>Store</label>
          <input value={`${r.store_data?.friendly_name}, ${r.store_data?.city}, ${r.store_data?.state}`} style={styles.input} disabled />
          <label style={styles.label}>Created By</label>
          <input value={`${r.user_data?.first_name} ${r.user_data?.last_name}` || ''} style={styles.input} disabled />
           
          <div style={styles.sectionTitle}>Opening Currency Count</div>
          <DenomRows jsonData={r.opening_currency_count} />
          <div style={styles.summaryRow}>
            <div style={{fontSize : '25px'}}>Total Opening Cash</div>
            <div style={{fontSize : '25px'}}>₹{numberDisplay(totalOpeningCash)}</div>
          </div>
          
          <div style={styles.sectionTitle}>Banking Currency Count</div>
          <DenomRows jsonData={r.banking_currency_count} />
          <div style={styles.summaryRow}>
            <div style={{fontSize : '25px'}}>Total Banking Cash</div>
            <div style={{fontSize : '25px'}}>₹{numberDisplay(totalBankingCash)}</div>
          </div>

          <label style={styles.label}>Milk Ordered (Litres)</label>
          <input value={numberDisplay(r.milk_ordered)} style={styles.input} disabled />

          <label style={styles.label}>Milk Received (Litres)</label>
          <input value={numberDisplay(r.milk_received)} style={styles.input} disabled />

          <label style={styles.label}>Status</label>
          <input value={r.status || ''} style={styles.input} disabled />
        </>
      );
    } else if (activeTab === 'closing'){
      const r = selectedReport;
      const sales = r.sales_breakdown_digital || {};
      const totalClosingCash = calculateTotal(r.closing_currency_count);
      const totalDigitalSettlements = calculateSalesTotal(sales);
      return (  
        <>
          <h3 style={styles.sectionTitle}>Closing Report Details</h3>
          <label style={styles.label}>Date</label>
          <input type="date" value={r.report_date} style={styles.input} disabled />
          <label style={styles.label}>Store</label>
          <input value={`${r.store_data?.friendly_name}, ${r.store_data?.city}, ${r.store_data?.state}`} style={styles.input} disabled />
          <label style={styles.label}>Created By</label>
          <input value={`${r.user_data?.first_name} ${r.user_data?.last_name}` || ''} style={styles.input} disabled />
          
          <div style={styles.sectionTitle}>Closing Currency Count</div>
          <DenomRows jsonData={r.closing_currency_count} />
          <div style={styles.summaryRow}>
            <div style={{fontSize : '25px'}}>Total Closing Cash</div>
            <div style={{fontSize : '25px'}}>₹{numberDisplay(totalClosingCash)}</div>
          </div>
          
          <h4 style={styles.sectionTitle}>Sales Breakdown (Digital)</h4>
          {Object.entries(sales).map(([key, value]) => (
            <div key={key}>
              <label style={styles.label}>{key}</label>
              <input value={numberDisplay(value)} style={styles.input} disabled />
            </div>
          ))}
          <div style={styles.summaryRow}>
            <div style={{fontSize : '25px'}}>Total Digital Settlements</div>
            <div style={{fontSize : '25px'}}>₹{numberDisplay(totalDigitalSettlements)}</div>
          </div>

          <label style={styles.label}>PetPooja Sales</label>
          <input value={numberDisplay(r.petpooja_sales)} style={styles.input} disabled />

          <label style={styles.label}>Expenses</label>
          <input value={numberDisplay(r.expenses)} style={styles.input} disabled />

          <label style={styles.label}>Pending Bills</label>
          <input value={numberDisplay(r.pending_bills)} style={styles.input} disabled />

          <label style={styles.label}>Dues Collected</label>
          <input value={numberDisplay(r.dues_received)} style={styles.input} disabled />

          <label style={styles.label}>Notes</label>
          <textarea value={r.notes || ''} style={{ ...styles.textarea }} disabled />

          <div style={styles.sectionTitle}>Summary</div>
          <div style={styles.summaryRow}>
            <div>Total Reported Sale</div>
            <div>₹{numberDisplay(r.summary_total_reported_sale)}</div>
          </div>
          <div style={styles.summaryRow}>
            <div>Total Physical Sale</div>
            <div>₹{numberDisplay(r.summary_total_physical_sale)}</div>
          </div>
          <div style={styles.summaryRow}>
            <div>Variance</div>
            <div>₹{numberDisplay(r.variance)}</div>
          </div>
        </>
      );
    } else if (activeTab === 'combined' && selectedReport) {
      const { opening_report, closing_report } = selectedReport;

      const date = opening_report?.report_date || closing_report?.report_date || "";
      const storeName = opening_report?.store_data?.friendly_name || closing_report?.store_data?.friendly_name || "Unknown";
      const userNameOpening = opening_report?.user_data ? `${opening_report.user_data.first_name} ${opening_report.user_data.last_name}` : "";
      const userNameClosing = closing_report?.user_data ? `${closing_report.user_data.first_name} ${closing_report.user_data.last_name}` : "";

      const totalOpeningCash = opening_report ? Object.entries(opening_report.opening_currency_count || {}).reduce((acc, [denom, count]) => acc + Number(denom) * Number(count), 0) : 0;
      const totalBankingCash = opening_report ? Object.entries(opening_report.banking_currency_count || {}).reduce((acc, [denom, count]) => acc + Number(denom) * Number(count), 0) : 0;
      const totalClosingCash = closing_report ? Object.entries(closing_report.closing_currency_count || {}).reduce((acc, [denom, count]) => acc + Number(denom) * Number(count), 0) : 0;
      const totalDigitalSettlements = closing_report ? Object.values(closing_report.sales_breakdown_digital || {}).reduce((acc, val) => acc + Number(val), 0) : 0;

      return (
        <>
          <h3 style={styles.sectionTitle}>Combined Report Details</h3>

          <div style={{ marginBottom: 24 }}>
            <label style={styles.label}>Date</label>
            <input type="date" value={date} style={styles.input} disabled />
            <label style={styles.label}>Store</label>
            <input value={`${storeName}`} style={styles.input} disabled />
            <label style={styles.label}>Opening Created By</label>
            <input value={userNameOpening || ''} style={styles.input} disabled />
            <label style={styles.label}>Closing Created By</label>
            <input value={userNameClosing || ''} style={styles.input} disabled />
          </div>

          <section style={{marginBottom: 36}}>
            <h4 style={styles.sectionTitle}>Opening Report</h4>
            {opening_report ? (
              <>
                <label style={styles.label}>Opening Currency Count</label>
                <DenomRows jsonData={opening_report.opening_currency_count} />
                <div style={styles.summaryRow}>
                  <div style={{ fontSize: '20px' }}>Total Opening Cash</div>
                  <div style={{ fontSize: '20px' }}>₹{numberDisplay(totalOpeningCash)}</div>
                </div>

                <label style={styles.label}>Banking Currency Count</label>
                <DenomRows jsonData={opening_report.banking_currency_count} />
                <div style={styles.summaryRow}>
                  <div style={{ fontSize: '20px' }}>Total Banking Cash</div>
                  <div style={{ fontSize: '20px' }}>₹{numberDisplay(totalBankingCash)}</div>
                </div>

                <label style={styles.label}>Milk Ordered (Litres)</label>
                <input value={numberDisplay(opening_report.milk_ordered)} style={styles.input} disabled />

                <label style={styles.label}>Milk Received (Litres)</label>
                <input value={numberDisplay(opening_report.milk_received)} style={styles.input} disabled />

                <label style={styles.label}>Status</label>
                <input value={opening_report.status || ''} style={styles.input} disabled />
              </>
            ) : (
              <div>No opening report available</div>
            )}
          </section>

          <section>
            <h4 style={styles.sectionTitle}>Closing Report</h4>
            {closing_report ? (
              <>
                <label style={styles.label}>Closing Currency Count</label>
                <DenomRows jsonData={closing_report.closing_currency_count} />
                <div style={styles.summaryRow}>
                  <div style={{ fontSize: '20px' }}>Total Closing Cash</div>
                  <div style={{ fontSize: '20px' }}>₹{numberDisplay(totalClosingCash)}</div>
                </div>

                <h5 style={{ marginTop: 16 }}>Sales Breakdown (Digital)</h5>
                {Object.entries(closing_report.sales_breakdown_digital || {}).map(([key, value]) => (
                  <div key={key}>
                    <label style={styles.label}>{key}</label>
                    <input value={numberDisplay(value)} style={styles.input} disabled />
                  </div>
                ))}
                <div style={styles.summaryRow}>
                  <div style={{ fontSize: '20px' }}>Total Digital Settlements</div>
                  <div style={{ fontSize: '20px' }}>₹{numberDisplay(totalDigitalSettlements)}</div>
                </div>

                <label style={styles.label}>PetPooja Sales</label>
                <input value={numberDisplay(closing_report.petpooja_sales)} style={styles.input} disabled />

                <label style={styles.label}>Expenses</label>
                <input value={numberDisplay(closing_report.expenses)} style={styles.input} disabled />

                <label style={styles.label}>Pending Bills</label>
                <input value={numberDisplay(closing_report.pending_bills)} style={styles.input} disabled />

                <label style={styles.label}>Dues Collected</label>
                <input value={numberDisplay(closing_report.dues_received)} style={styles.input} disabled />

                <label style={styles.label}>Notes</label>
                <textarea value={closing_report.notes || ''} style={{ ...styles.textarea }} disabled />

                <div style={styles.sectionTitle}>Summary</div>
                <div style={styles.summaryRow}>
                  <div>Total Reported Sale</div>
                  <div>₹{numberDisplay(closing_report.summary_total_reported_sale)}</div>
                </div>
                <div style={styles.summaryRow}>
                  <div>Total Physical Sale</div>
                  <div>₹{numberDisplay(closing_report.summary_total_physical_sale)}</div>
                </div>
                <div style={styles.summaryRow}>
                  <div>Variance</div>
                  <div>₹{numberDisplay(closing_report.variance)}</div>
                </div>
              </>
            ) : (
              <div>No closing report available</div>
            )}
          </section>
        </>
      );
    }
  };

  const exportToExcel = async () => {
    setLoading(true);
    try {
      const reports = await fetchAllFilteredData();
      const data = [];

      reports.forEach(report => {
        if (activeTab === "banking") {
          data.push({
            "Report ID": report.id,
            Date: report.report_date,
            "Store": report.store_data ? `${report.store_data.friendly_name}, ${report.store_data.city}, ${report.store_data.state}` : "",
            "Created By": report.user_data ? `${report.user_data.first_name} ${report.user_data.last_name}` : "",
            "Banking Cash Total": calculateTotal(report.banking_currency_count),
            "Status": report.status || "",
          });
        }
        else if (activeTab === "opening") {
          data.push({
            "Report ID": report.id,
            Date: report.report_date,
            "Store": report.store_data ? `${report.store_data.friendly_name}, ${report.store_data.city}, ${report.store_data.state}` : "",
            "Created By": report.user_data ? `${report.user_data.first_name} ${report.user_data.last_name}` : "",
            "Opening Cash Total": calculateTotal(report.opening_currency_count),
            "Banking Cash Total": calculateTotal(report.banking_currency_count),
            "Milk Ordered (Ltrs)": numberDisplay(report.milk_ordered),
            "Milk Received (Ltrs)": numberDisplay(report.milk_received),
            "Extra Milk (Ltrs)": numberDisplay(report.milk_ordered - report.milk_received),
            "Status": report.status || "",
          });
        } else if (activeTab === "closing") {
          data.push({
            "Report ID": report.id,
            Date: report.report_date,
            "Store": report.store_data ? `${report.store_data.friendly_name}, ${report.store_data.city}, ${report.store_data.state}` : "",
            "Created By": report.user_data ? `${report.user_data.first_name} ${report.user_data.last_name}` : "",
            "Closing Cash Total": calculateTotal(report.closing_currency_count),
            "Digital Settlements Total": Object.values(report.sales_breakdown_digital || {}).reduce((a, v) => a + v, 0),
            "PetPooja Sales": numberDisplay(report.petpooja_sales),
            "Expenses": numberDisplay(report.expenses),
            "Pending Bills": numberDisplay(report.pending_bills),
            "Dues Received": numberDisplay(report.dues_received),
            "Notes": report.notes || "",
            "Total Reported Sale": numberDisplay(report.summary_total_reported_sale),
            "Total Physical Sale": numberDisplay(report.summary_total_physical_sale),
            "Variance": numberDisplay(report.variance),
          });
        } else if (activeTab === "combined") {
          const opening = report.opening_report || {};
          const closing = report.closing_report || {};
          data.push({
            "Date": opening.report_date || closing.report_date || "",
            "Store": 
              opening.store_data ? `${opening.store_data.friendly_name}, ${opening.store_data.city}, ${opening.store_data.state}` :
              closing.store_data ? `${closing.store_data.friendly_name}, ${closing.store_data.city}, ${closing.store_data.state}` : "",
            "Opening Created By":
              opening.user_data ? `${opening.user_data.first_name} ${opening.user_data.last_name}` : "",
               "Closing Created By": closing.user_data ? `${closing.user_data.first_name} ${closing.user_data.last_name}` : "",
            // Opening report fields
            "Opening Cash Total": calculateTotal(opening.opening_currency_count),
            "Banking Cash Total": calculateTotal(opening.banking_currency_count),
            "Milk Ordered (Ltrs)": numberDisplay(opening.milk_ordered),
            "Milk Received (Ltrs)": numberDisplay(opening.milk_received),
            "Opening Status": opening.status || "",

            // Closing report fields
            "Closing Cash Total": calculateTotal(closing.closing_currency_count),
            "Digital Settlements Total": Object.values(closing.sales_breakdown_digital || {}).reduce((a, v) => a + v, 0),
            "PetPooja Sales": numberDisplay(closing.petpooja_sales),
            "Expenses": numberDisplay(closing.expenses),
            "Pending Bills": numberDisplay(closing.pending_bills),
            "Dues Received": numberDisplay(closing.dues_received),
            "Notes": closing.notes || "",
            "Total Reported Sale": numberDisplay(closing.summary_total_reported_sale),
            "Total Physical Sale": numberDisplay(closing.summary_total_physical_sale),
            "Variance": numberDisplay(closing.variance),
          });
        }
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Reports");
      XLSX.writeFile(workbook, "reports_export.xlsx");
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = async () => {
    setLoading(true);
    try {
      const reports = await fetchAllFilteredData();
      const doc = new jsPDF();
      
      if (activeTab === "banking") { 
        const columns = [
          { header: "Report ID", dataKey: "id" },
          { header: "Date", dataKey: "date" },
          { header: "Store", dataKey: "store" },
          { header: "Created By", dataKey: "createdBy" },
          { header: "Banking Cash Total", dataKey: "bankingTotal" },
          { header: "Status", dataKey: "status" },
        ];

        const rows = reports.map(report => ({
          id: report.id,
          date: report.report_date,
          store: report.store_data ? `${report.store_data.friendly_name}, ${report.store_data.city}, ${report.store_data.state}` : "",
          createdBy: report.user_data ? `${report.user_data.first_name} ${report.user_data.last_name}` : "",
          bankingTotal: calculateTotal(report.banking_currency_count),
          status: report.status || "",
        }));

        autoTable(doc, { columns, body: rows, margin: { top: 10 } });
        doc.save("banking_reports.pdf");
      }
      else if (activeTab === "opening") {
        const columns = [
          { header: "Report ID", dataKey: "id" },
          { header: "Date", dataKey: "date" },
          { header: "Store", dataKey: "store" },
          { header: "Created By", dataKey: "createdBy" },
          { header: "Opening Cash Total", dataKey: "openingTotal" },
          { header: "Banking Cash Total", dataKey: "bankingTotal" },
          { header: "Milk Ordered", dataKey: "milkOrdered" },
          { header: "Milk Received", dataKey: "milkReceived" },
          { header: "Extra Milk", dataKey: "milkExtra" },
          { header: "Status", dataKey: "status" },
        ];

        const rows = reports.map(report => ({
          id: report.id,
          date: report.report_date,
          store: report.store_data ? `${report.store_data.friendly_name}, ${report.store_data.city}, ${report.store_data.state}` : "",
          createdBy: report.user_data ? `${report.user_data.first_name} ${report.user_data.last_name}` : "",
          openingTotal: calculateTotal(report.opening_currency_count),
          bankingTotal: calculateTotal(report.banking_currency_count),
          milkOrdered: numberDisplay(report.milk_ordered),
          milkReceived: numberDisplay(report.milk_received),
          milkExtra: numberDisplay(report.milk_ordered - report.milk_received),
          status: report.status || "",
        }));

        autoTable(doc, { columns, body: rows, margin: { top: 10 } });
        doc.save("opening_reports.pdf");
      } else if (activeTab === "closing") {
        const columns = [
          { header: "Report ID", dataKey: "id" },
          { header: "Date", dataKey: "date" },
          { header: "Store", dataKey: "store" },
          { header: "Created By", dataKey: "createdBy" },
          { header: "Closing Cash Total", dataKey: "closingTotal" },
          { header: "Digital Settlements", dataKey: "digitalTotal" },
          { header: "PetPooja Sales", dataKey: "petPoojaSales" },
          { header: "Expenses", dataKey: "expenses" },
          { header: "Pending Bills", dataKey: "pendingBills" },
          { header: "Dues Received", dataKey: "duesReceived" },
          { header: "Total Reported Sale", dataKey: "reportSale" },
          { header: "Variance", dataKey: "variance" },
        ];

        const rows = reports.map(report => ({
          id: report.id,
          date: report.report_date,
          store: report.store_data ? `${report.store_data.friendly_name}, ${report.store_data.city}, ${report.store_data.state}` : "",
          createdBy: report.user_data ? `${report.user_data.first_name} ${report.user_data.last_name}` : "",
          closingTotal: calculateTotal(report.closing_currency_count),
          digitalTotal: Object.values(report.sales_breakdown_digital || {}).reduce((a, v) => a + v, 0),
          petPoojaSales: numberDisplay(report.petpooja_sales),
          expenses: numberDisplay(report.expenses),
          pendingBills: numberDisplay(report.pending_bills),
          duesReceived: numberDisplay(report.dues_received),
          reportSale: numberDisplay(report.summary_total_reported_sale),
          variance: numberDisplay(report.variance),
        }));

        autoTable(doc, { columns, body: rows, margin: { top: 10 } });
        doc.save("closing_reports.pdf");
      } else if (activeTab === "combined") {
        const columns = [
          { header: "Date", dataKey: "date" },
          { header: "Store", dataKey: "store" },
          { header: "Opening Total", dataKey: "openingTotal" },
          { header: "Banking Total", dataKey: "bankingTotal" },
          { header: "Milk Ordered", dataKey: "milkOrdered" },
          { header: "Milk Received", dataKey: "milkReceived" },
          { header: "Closing Total", dataKey: "closingTotal" },
          { header: "Digital Settlements", dataKey: "digitalTotal" },
          { header: "PetPooja Sales", dataKey: "petPoojaSales" },
          { header: "Expenses", dataKey: "expenses" },
          { header: "Reported Sale", dataKey: "reportSale" },
          { header: "Variance", dataKey: "variance" },
        ];

        const rows = reports.map(report => {
          const opening = report.opening_report || {};
          const closing = report.closing_report || {};
          return {
            date: opening.report_date || closing.report_date || "",
            store:
              opening.store_data
                ? `${opening.store_data.friendly_name}, ${opening.store_data.city}, ${opening.store_data.state}`
                : closing.store_data
                ? `${closing.store_data.friendly_name}, ${closing.store_data.city}, ${closing.store_data.state}`
                : "",
            // Opening data
            openingTotal: calculateTotal(opening.opening_currency_count),
            bankingTotal: calculateTotal(opening.banking_currency_count),
            milkOrdered: numberDisplay(opening.milk_ordered),
            milkReceived: numberDisplay(opening.milk_received),
            // Closing data
            closingTotal: calculateTotal(closing.closing_currency_count),
            digitalTotal: Object.values(closing.sales_breakdown_digital || {}).reduce((a, v) => a + v, 0),
            petPoojaSales: numberDisplay(closing.petpooja_sales),
            expenses: numberDisplay(closing.expenses),
            reportSale: numberDisplay(closing.summary_total_reported_sale),
            variance: numberDisplay(closing.variance),
          };
        });

        autoTable(doc, { columns, body: rows, margin: { top: 10 } });
        doc.save("combined_reports.pdf");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader/>}
      
      <div style={styles.pageBody}>
        <h1 style={{ marginBottom: 24, color: "#21325b" }}>Reports</h1>
        
        <Row>
          <FilterBar
            placeholder="Search Reports..."
            sortFields={[
              { value: "report_date", label: "Report Date" },
              { value: "store_data.friendly_name", label: "Store Name" },
              { value: "user_data.first_name", label: "Created By First Name" },
              { value: "user_data.last_name", label: "Created By Last Name" },
            ]}
            stores={stores}
            onTextFilter={handleTextFilter}
            onDateFilter={handleDateFilter}
            onSortChange={handleSortChange}
            onResetFilters={handleResetFilters}
            onExportPDF={exportToPDF}
            onExportExcel={exportToExcel}
            onStoreFilter={handleStoreFilter}
          />
        </Row>
        
        <div style={styles.tabBar}>
          <button style={styles.tabButton(activeTab === 'opening')} onClick={() => handleTabChange('opening')}>
            Opening Reports
          </button>
          <button style={styles.tabButton(activeTab === 'banking')} onClick={() => handleTabChange('banking')}>
            Banking Reports
          </button>
          <button style={styles.tabButton(activeTab === 'closing')} onClick={() => handleTabChange('closing')}>
            Closing Reports
          </button>
          <button style={styles.tabButton(activeTab === 'combined')} onClick={() => handleTabChange('combined')}>
            Combined Reports
          </button>
        </div>

        {error && <div style={{ color: 'red', fontWeight: '700', fontSize: 16 }}>{error}</div>}

        {!loading && !error && (
          <>
            <table style={styles.table} role="grid" aria-label="Reports Table">
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Store</th>
                  {activeTab !== 'combined' && <th style={styles.th}>Created By</th>}
                  {activeTab === 'combined' && <th style={styles.th}>Opening Created By</th>}
                  {activeTab === 'combined' && <th style={styles.th}>Closing Created By</th>}
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeTab === 'opening' || activeTab === 'banking' ? renderOpeningRows() : activeTab === 'closing' ? renderClosingRows() : renderCombinedRows()}
              </tbody>
            </table>
            
            {/* Pagination Controls with Row Selector */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, padding: '10px 0', borderTop: '1px solid #e0e0e0' }}>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{ color: '#555', fontWeight: '600' }}>
                  Showing {totalRecords === 0 ? 0 : ((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalRecords)} of {totalRecords} entries
                </div>
                
                {/* Row Limiter Select Box */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ color: '#555', fontWeight: '600', fontSize: '14px' }}>Rows per page:</label>
                  <select 
                    value={limit} 
                    onChange={handleLimitChange}
                    style={{ padding: '6px', borderRadius: '6px', border: '1px solid #ccc', outline: 'none', cursor: 'pointer', fontWeight: '600' }}
                  >
                    <option value={10}>10</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button 
                  style={currentPage === 1 ? styles.pageButtonDisabled : styles.pageButton} 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', color: '#0056b3' }}>
                  Page {currentPage}
                </div>
                <button 
                  style={currentPage * limit >= totalRecords ? styles.pageButtonDisabled : styles.pageButton} 
                  onClick={() => setCurrentPage(p => p + 1)} 
                  disabled={currentPage * limit >= totalRecords}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}

        {viewModalOpen && (
          <div style={styles.modalOverlay} onClick={closeViewModal} role="dialog" aria-modal="true">
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div id="modalTitle" style={{ fontWeight: "900", fontSize: 24, marginBottom: 24 }}>
                {activeTab === "opening" ? "Opening Report Details" : "Closing Report Details"}
              </div>
              {ModalContent()}
              <button
                style={{ marginTop: 24, padding: '10px 20px', cursor: 'pointer', backgroundColor: '#0056b3', border: 'none', borderRadius: 8, color: 'white', fontWeight: '700', fontSize: 16 }}
                onClick={closeViewModal}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default ReportsPage;
